import { useState, useCallback } from 'react';
import { Form, FormInstance } from '@bancobarigui/barihaus';

import { useAlertContext } from '@/contexts/alert.context';
import { useErrorHandler } from './useErrorHandler';

export interface UseModalFormOptions<TFormData, TResult = void> {
  onSubmit: (values: TFormData) => Promise<TResult>;
  onSuccess?: (result: TResult) => void;
  onClose?: () => void;
  successMessage?: string | ((result: TResult) => string);
  errorMessage?: string;
  resetOnSuccess?: boolean;
  closeOnSuccess?: boolean;
}

export interface UseModalFormReturn<TFormData> {
  form: FormInstance<TFormData>;
  isSubmitting: boolean;
  handleFormSubmit: (values: TFormData) => Promise<void>;
  resetForm: () => void;
}

export function useModalForm<TFormData = Record<string, unknown>, TResult = void>({
  onSubmit,
  onSuccess,
  onClose,
  successMessage,
  errorMessage = 'Erro ao processar',
  resetOnSuccess = true,
  closeOnSuccess = true,
}: UseModalFormOptions<TFormData, TResult>): UseModalFormReturn<TFormData> {
  const [form] = Form.useForm<TFormData>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { notify } = useAlertContext();
  const { handleError } = useErrorHandler();

  const resetForm = useCallback(() => {
    form.resetFields();
  }, [form]);

  const handleFormSubmit = useCallback(async (values: TFormData) => {
    setIsSubmitting(true);
    try {
      const result = await onSubmit(values);

      if (successMessage) {
        const message = typeof successMessage === 'function'
          ? successMessage(result)
          : successMessage;
        notify({ message, type: 'success' });
      }

      if (resetOnSuccess) {
        resetForm();
      }

      if (closeOnSuccess && onClose) {
        onClose();
      }

      if (onSuccess) {
        onSuccess(result);
      }
    } catch (error) {
      handleError(error, errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  }, [onSubmit, onSuccess, onClose, successMessage, errorMessage, resetOnSuccess, closeOnSuccess, notify, handleError, resetForm]);

  return {
    form,
    isSubmitting,
    handleFormSubmit,
    resetForm,
  };
}
